import { useState } from "react";
import { Link } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetEnrichmentGovernanceSummary,
  getGetEnrichmentGovernanceSummaryQueryKey,
  useGetAuthorNotabilityReviews,
  getGetAuthorNotabilityReviewsQueryKey,
  useGetEnrichmentBlastRadius,
  getGetEnrichmentBlastRadiusQueryKey,
  useOverrideAuthorNotability,
  useCreateEnrichmentWorkItem,
  AuthorNotabilityOverrideTier,
  type EnrichmentWorkItemRequest,
} from "@workspace/api-client-react";
import { useAuthEnabled } from "@/lib/authQuery";

const TIER_OPTIONS = [
  { value: "canonical", label: "Canonical" },
  { value: "notable", label: "Notable" },
  { value: "marginal", label: "Marginal" },
  { value: "unknown", label: "Unknown" },
] as const;

export function GovernancePage() {
  const queryClient = useQueryClient();
  const authed = useAuthEnabled();

  const summaryQuery = useGetEnrichmentGovernanceSummary({
    query: {
      queryKey: getGetEnrichmentGovernanceSummaryQueryKey(),
      enabled: authed,
      refetchInterval: 30_000,
    },
  });

  const reviewsQuery = useGetAuthorNotabilityReviews({ limit: 50 }, {
    query: { queryKey: getGetAuthorNotabilityReviewsQueryKey({ limit: 50 }), enabled: authed },
  });

  const blastQuery = useGetEnrichmentBlastRadius({
    query: {
      queryKey: getGetEnrichmentBlastRadiusQueryKey(),
      enabled: authed,
      refetchInterval: 30_000,
    },
  });

  const { mutate: overrideNotability, isPending: isOverridePending } = useOverrideAuthorNotability({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetAuthorNotabilityReviewsQueryKey({ limit: 50 }) });
        queryClient.invalidateQueries({ queryKey: getGetEnrichmentGovernanceSummaryQueryKey() });
        setOverrideAuthor("");
        setOverrideRationale("");
        setOverrideTier("canonical");
      }
    }
  });

  const { mutate: createWorkItem, isPending: isWorkItemPending, isError: isWorkItemError } = useCreateEnrichmentWorkItem({
    mutation: {
      onSuccess: (request) => {
        queryClient.invalidateQueries({ queryKey: getGetEnrichmentGovernanceSummaryQueryKey() });
        setLastWorkRequest(request);
        setWorkItemQuoteId("");
        setWorkItemAuthor("");
        setWorkItemReason("");
      }
    }
  });

  // Override Form State
  const [overrideAuthor, setOverrideAuthor] = useState("");
  const [overrideRationale, setOverrideRationale] = useState("");
  const [overrideTier, setOverrideTier] = useState<AuthorNotabilityOverrideTier>("canonical");

  // Work Item Form State
  const [workItemQuoteId, setWorkItemQuoteId] = useState("");
  const [workItemAuthor, setWorkItemAuthor] = useState("");
  const [workItemReason, setWorkItemReason] = useState("");
  const [lastWorkRequest, setLastWorkRequest] = useState<EnrichmentWorkItemRequest | null>(null);
  const hasQuoteTarget = Boolean(workItemQuoteId.trim());
  const hasAuthorTarget = Boolean(workItemAuthor.trim());
  const hasOneWorkTarget = hasQuoteTarget !== hasAuthorTarget;

  const handleOverride = (e: React.FormEvent) => {
    e.preventDefault();
    if (!overrideAuthor.trim() || !overrideRationale.trim()) return;
    overrideNotability({
      authorName: overrideAuthor.trim(),
      data: { tier: overrideTier, rationale: overrideRationale.trim() },
    });
  };

  const handleCreateWorkItem = (e: React.FormEvent) => {
    e.preventDefault();
    const quoteId = Number.parseInt(workItemQuoteId, 10);
    if (!workItemReason.trim() || !hasOneWorkTarget || (hasQuoteTarget && (!Number.isInteger(quoteId) || quoteId < 1))) return;

    createWorkItem({
      data: {
        quoteId: hasQuoteTarget ? quoteId : null,
        authorName: workItemAuthor.trim() || null,
        reasonCode: workItemReason.trim(),
        priority: 50,
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link 
          href="/harvest"
          className="rounded-full bg-stone-100 px-4 py-1.5 text-sm font-medium text-stone-600 transition hover:bg-stone-200 active:scale-[0.98]"
        >
          Harvest Queue
        </Link>
        <Link 
          href="/harvest/governance"
          className="rounded-full bg-stone-900 px-4 py-1.5 text-sm font-medium text-white shadow-sm transition hover:bg-stone-800 active:scale-[0.98]"
        >
          Governance
        </Link>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        {/* Left Column: Summary & Work Item form */}
        <div className="space-y-6 xl:col-span-1">
          
          <section className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
            <h3 className="text-sm font-bold text-stone-900 uppercase tracking-wider mb-4">Governance Summary</h3>
            {summaryQuery.isLoading ? (
              <div className="space-y-3">
                <div className="h-4 w-1/2 animate-pulse rounded bg-stone-100" />
                <div className="h-4 w-3/4 animate-pulse rounded bg-stone-100" />
                <div className="h-4 w-2/3 animate-pulse rounded bg-stone-100" />
              </div>
            ) : summaryQuery.data ? (
              <div className="space-y-4 text-sm">
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <span className="text-stone-500 font-medium">Policy Version</span>
                  <span className="font-mono text-stone-900 bg-stone-100 px-1.5 py-0.5 rounded">{summaryQuery.data.policyVersion}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <span className="text-stone-500 font-medium">Shadow Mode</span>
                  {summaryQuery.data.shadowMode ? (
                    <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full font-semibold text-xs">Active</span>
                  ) : (
                    <span className="text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full font-semibold text-xs">Inactive</span>
                  )}
                </div>
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <span className="text-stone-500 font-medium">Author Overrides</span>
                  <span className="font-semibold text-stone-900">{summaryQuery.data.activeAuthorOverrides}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <span className="text-stone-500 font-medium">Recorded Decisions</span>
                  <span className="font-semibold text-stone-900">{summaryQuery.data.recordedAuthorDecisions}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <span className="text-stone-500 font-medium">Classifications</span>
                  <span className="font-semibold text-stone-900">{summaryQuery.data.recordedClassifications}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <span className="text-stone-500 font-medium">Projections</span>
                  <span className="font-semibold text-stone-900">{summaryQuery.data.recordedProjections}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <span className="text-stone-500 font-medium">Queued Work</span>
                  <span className="font-semibold text-stone-900">{summaryQuery.data.queuedWorkItems}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <span className="text-stone-500 font-medium">In-flight Work</span>
                  <span className="font-semibold text-amber-700">{summaryQuery.data.inFlightWorkItems}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <span className="text-stone-500 font-medium">Awaiting Durable Save</span>
                  <span className="font-semibold text-amber-700">{summaryQuery.data.modelCompletedWorkItems}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-stone-100">
                  <span className="text-stone-500 font-medium">Failed Work</span>
                  <span className="font-semibold text-rose-700">{summaryQuery.data.failedWorkItems}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-stone-500 font-medium">Completed Work</span>
                  <span className="font-semibold text-emerald-700">{summaryQuery.data.completedWorkItems}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-stone-500">Failed to load summary.</p>
            )}
          </section>

          <section className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
            <h3 className="text-sm font-bold text-stone-900 uppercase tracking-wider mb-4">Targeted Reprocessing</h3>
            <p className="text-xs text-stone-500 mb-5 leading-relaxed">
              Choose one target. An author request fans out to every matching quote, while duplicate active requests are coalesced.
            </p>
            <form onSubmit={handleCreateWorkItem} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-stone-700 mb-1.5">Quote ID</label>
                <input
                  type="number"
                  value={workItemQuoteId}
                  onChange={(e) => setWorkItemQuoteId(e.target.value)}
                  placeholder="e.g. 42"
                  className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm focus:border-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-700 mb-1.5">Author Name</label>
                <input
                  type="text"
                  value={workItemAuthor}
                  onChange={(e) => setWorkItemAuthor(e.target.value)}
                  placeholder="e.g. Marcus Aurelius"
                  className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm focus:border-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-700 mb-1.5">Reason Code</label>
                <input
                  type="text"
                  required
                  value={workItemReason}
                  onChange={(e) => setWorkItemReason(e.target.value)}
                  placeholder="e.g. recheck_attribution"
                  className="w-full rounded-xl border border-stone-200 px-3 py-2 text-sm focus:border-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400"
                />
              </div>
              <button
                type="submit"
                disabled={isWorkItemPending || !workItemReason.trim() || !hasOneWorkTarget}
                className="w-full rounded-xl bg-stone-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-stone-800 active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
              >
                {isWorkItemPending ? "Queueing..." : "Queue Reprocessing"}
              </button>
            </form>
            {!hasOneWorkTarget && (hasQuoteTarget || hasAuthorTarget) && (
              <p className="mt-3 text-xs text-rose-700">Choose either a quote ID or an author, not both.</p>
            )}
            {isWorkItemError && (
              <p className="mt-3 text-xs text-rose-700">The refresh request could not be queued. Check the target and try again.</p>
            )}
            {lastWorkRequest && (
              <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50 p-3 text-xs text-emerald-900">
                <p className="font-semibold">
                  {lastWorkRequest.enqueuedQuotes} of {lastWorkRequest.targetedQuotes} matching quote{lastWorkRequest.targetedQuotes === 1 ? "" : "s"} queued.
                </p>
                <p className="mt-1">
                  {lastWorkRequest.existingActiveQuotes} already active · {lastWorkRequest.inFlightWorkItems} in flight · {lastWorkRequest.failedWorkItems} failed · {lastWorkRequest.completedWorkItems} completed
                </p>
              </div>
            )}
          </section>
        </div>

        {/* Right Columns: Blast Radius & Reviews */}
        <div className="space-y-6 xl:col-span-2">
          
          <section className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm overflow-hidden">
            <h3 className="text-sm font-bold text-stone-900 uppercase tracking-wider mb-1">Shadow Policy Blast Radius</h3>
            <p className="text-xs text-stone-500 mb-6">Uses the same durable classifications and active author decisions as the policy rebuild.</p>
            
            {blastQuery.isLoading ? (
              <div className="h-48 animate-pulse rounded-xl bg-stone-50" />
            ) : blastQuery.data ? (
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
                  <div className="rounded-xl border border-stone-100 bg-stone-50 p-4">
                    <p className="text-2xl font-bold text-stone-900">{blastQuery.data.totalQuotes}</p>
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-stone-500">All Quotes</p>
                  </div>
                  <div className="rounded-xl border border-stone-100 bg-stone-50 p-4">
                    <p className="text-2xl font-bold text-stone-900">{blastQuery.data.unchanged}</p>
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-stone-500">Unchanged</p>
                  </div>
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
                    <p className="text-2xl font-bold text-emerald-700">{blastQuery.data.wouldApprove}</p>
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-emerald-600">Would Approve</p>
                  </div>
                  <div className="rounded-xl border border-rose-100 bg-rose-50 p-4">
                    <p className="text-2xl font-bold text-rose-700">{blastQuery.data.wouldWithdrawPublic}</p>
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-rose-600">Would Withdraw</p>
                  </div>
                  <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
                    <p className="text-2xl font-bold text-amber-700">{blastQuery.data.needsFacts}</p>
                    <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-amber-700">Need Facts</p>
                  </div>
                </div>
                {(blastQuery.data.needsFacts > 0 || (summaryQuery.data?.queuedWorkItems ?? 0) > 0 || (summaryQuery.data?.inFlightWorkItems ?? 0) > 0 || (summaryQuery.data?.failedWorkItems ?? 0) > 0) && (
                  <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs leading-relaxed text-amber-900">
                    <span className="font-semibold">Shadow baseline incomplete.</span>{" "}
                    {blastQuery.data.needsFacts} quote{blastQuery.data.needsFacts === 1 ? "" : "s"} need current classification facts;{" "}
                    {summaryQuery.data?.queuedWorkItems ?? 0} queued, {summaryQuery.data?.inFlightWorkItems ?? 0} in flight, and{" "}
                    {summaryQuery.data?.failedWorkItems ?? 0} failed refresh{(summaryQuery.data?.failedWorkItems ?? 0) === 1 ? "" : "es"} remain. Wait for or resolve this work before human review.
                  </div>
                )}

                <div className="overflow-x-auto rounded-xl border border-stone-200">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-stone-50 border-b border-stone-200 text-xs font-semibold uppercase tracking-wider text-stone-500">
                      <tr>
                        <th className="px-4 py-3">Quote</th>
                        <th className="px-4 py-3">Author</th>
                        <th className="px-4 py-3">Current</th>
                        <th className="px-4 py-3">Proposed</th>
                        <th className="px-4 py-3">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {blastQuery.data.samples.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-6 text-center text-stone-500 text-sm">No impact samples available.</td>
                        </tr>
                      ) : (
                        blastQuery.data.samples.map(sample => (
                          <tr key={sample.quoteId} className="hover:bg-stone-50/50 transition-colors">
                            <td className="px-4 py-3 font-mono text-xs text-stone-600">#{sample.quoteId}</td>
                            <td className="px-4 py-3 font-medium text-stone-900">{sample.author}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-col gap-1 items-start">
                                <span className="bg-stone-100 text-stone-600 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-widest">{sample.currentStatus}</span>
                                <span className="text-[10px] text-stone-500">{sample.currentVisibility}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-col gap-1 items-start">
                                <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-widest">{sample.proposedStatus}</span>
                                <span className="text-[10px] text-amber-700/70">{sample.proposedVisibility}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-stone-500 text-xs truncate max-w-[150px]" title={sample.reasonCode}>
                              {sample.reasonCode}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-sm text-stone-500">Failed to load blast radius data.</p>
            )}
          </section>

          <section className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
            <h3 className="text-sm font-bold text-stone-900 uppercase tracking-wider mb-1">Author Notability Review</h3>
            <p className="text-xs text-stone-500 mb-6">Force override author tiers to bypass AI automated classification.</p>
            
            <form onSubmit={handleOverride} className="mb-8 grid gap-4 sm:grid-cols-4 items-end bg-stone-50 p-4 rounded-xl border border-stone-100">
              <div className="sm:col-span-1">
                <label className="block text-xs font-semibold text-stone-700 mb-1.5">Author Name</label>
                <input
                  type="text"
                  required
                  value={overrideAuthor}
                  onChange={(e) => setOverrideAuthor(e.target.value)}
                  placeholder="Author..."
                  className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm bg-white focus:border-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-xs font-semibold text-stone-700 mb-1.5">Tier</label>
                <select
                  value={overrideTier}
                  onChange={(e) => setOverrideTier(e.target.value as AuthorNotabilityOverrideTier)}
                  className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm bg-white focus:border-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400"
                >
                  {TIER_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-1">
                <label className="block text-xs font-semibold text-stone-700 mb-1.5">Rationale</label>
                <input
                  type="text"
                  required
                  value={overrideRationale}
                  onChange={(e) => setOverrideRationale(e.target.value)}
                  placeholder="Reason for override..."
                  className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm bg-white focus:border-stone-400 focus:outline-none focus:ring-1 focus:ring-stone-400"
                />
              </div>
              <div className="sm:col-span-1">
                <button
                  type="submit"
                  disabled={isOverridePending || !overrideAuthor.trim() || !overrideRationale.trim()}
                  className="w-full rounded-lg bg-stone-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-stone-800 active:scale-[0.98] disabled:opacity-50 disabled:active:scale-100"
                >
                  {isOverridePending ? "Saving..." : "Force Override"}
                </button>
              </div>
            </form>

            {reviewsQuery.isLoading ? (
              <div className="h-32 animate-pulse rounded-xl bg-stone-50" />
            ) : reviewsQuery.data ? (
              <div className="space-y-4">
                <h4 className="text-xs font-semibold text-stone-700 uppercase tracking-wide">Recent Reviews</h4>
                <div className="divide-y divide-stone-100">
                  {reviewsQuery.data.length === 0 ? (
                    <p className="text-sm text-stone-500 py-4">No reviews recorded yet.</p>
                  ) : (
                    reviewsQuery.data.map((review, idx) => (
                      <div key={idx} className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-stone-900 text-sm">{review.authorName}</span>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                              review.isOverride ? "bg-purple-100 text-purple-700" : "bg-stone-100 text-stone-600"
                            }`}>
                              {review.isOverride ? "Override" : "AI"}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                              review.tier === "canonical" ? "bg-emerald-100 text-emerald-700" :
                              review.tier === "notable" ? "bg-sky-100 text-sky-700" :
                              review.tier === "marginal" ? "bg-amber-100 text-amber-700" :
                              "bg-rose-100 text-rose-700"
                            }`}>
                              {review.tier}
                            </span>
                          </div>
                          <p className="text-xs text-stone-500">
                            {review.rationale || "No rationale provided."} <span className="mx-1 text-stone-300">|</span> 
                            {review.quoteCount} quotes <span className="mx-1 text-stone-300">|</span> 
                            Source: {review.source}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <span className="text-[10px] text-stone-400 uppercase tracking-widest font-mono">
                            {new Date(review.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-stone-500">Failed to load reviews.</p>
            )}
          </section>

        </div>
      </div>
    </div>
  );
}
